<app-tab-toolbar [tabId]="'board'" [selectedYear]="selectedYear" [saveDisabled]="!hasChanges && !isBoardDetailsCarriedForward"
  [showOtherMeasurement]="true" [otherMeasurement]="otherMeasurement"
  [showKeywordsToggle]="true" [keywordsVisible]="showKeywords"
  (keywordsVisibleChange)="showKeywords = $event"
  (yearChange)="onYearChange($event)" (otherMeasurementChange)="onOtherMeasurementChange($event)"
  (save)="onSave()">
  @if (showYearWarning) {
    <div class="year-warning">
      <mat-icon>warning</mat-icon>
      @if (isBoardDetailsCarriedForward) {
        <span>There is no data for the selected year. Governance/Board Guidelines and Code of Ethics are carried forward from a previous year.</span>
      } @else {
        <span>There is no data for the selected year.</span>
      }
    </div>
  }
</app-tab-toolbar>

<div class="board-content" [formGroup]="form" [appReadOnlyTab]="'board'">

  <!-- Board of Directors (type 0) / Supervisory Board (type 1) -->
  <div class="board-section">
    <h3 class="section-title">{{ companyBoardType === 0 ? 'Board of Directors' : 'Supervisory Board' }}</h3>

    @if (boardDataSource.data.length === 0) {
      <div class="no-data-message">No data available</div>
    } @else {
    <div class="table-wrapper">
      <table mat-table [dataSource]="boardDataSource" class="board-table" formArrayName="rows"
             matSort #boardSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onBoardSort($event)">

        <!-- # Column -->
        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
        </ng-container>

        <!-- Name Column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
          <td mat-cell *matCellDef="let row">
            <span class="name-cell">
              <a [routerLink]="['/directors/edit', row.directorId]" [queryParams]="{ fromCompany: companyId, fromCompanyName: companyName(), fromYear: selectedYear }" class="director-link-btn" title="Edit director" (click)="$event.stopPropagation()">
                <mat-icon>open_in_new</mat-icon>
              </a>
              {{ row.directorFullName }}
            </span>
          </td>
        </ng-container>

        <!-- Classification Column -->
        <ng-container matColumnDef="classification">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Classification</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="classification" class="ht-select" [title]="getClassificationLabel(row.classification)">
              <option value=""></option>
              @for (opt of classificationOptions; track opt.value) {
                <option [value]="opt.value" [title]="opt.label">{{ opt.label }}</option>
              }
            </select>
          </td>
        </ng-container>

        <!-- Occupation Column -->
        <ng-container matColumnDef="occupation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="occupationId" class="ht-select" [title]="getOccupationName(row.occupationId)">
              <option value=""></option>
              @for (occ of occupations; track occ.id) {
                <option [value]="occ.id" [title]="occ.name">{{ occ.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <!-- Company Representative Column -->
        <ng-container matColumnDef="companyRepresentative">
          <th mat-header-cell *matHeaderCellDef>C.R.</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-center">
            @if (!isReadOnly() && isCompanyRepresentativeOccupation(rowsArray.at(i).get('occupationId')?.value)) {
              <button mat-icon-button type="button" class="cr-btn"
                      [class.has-value]="hasCompanyRepresentative(rowsArray.at(i))"
                      [title]="rowsArray.at(i).get('companyRepresentative')?.value || 'Add company representative'"
                      (click)="onEditCompanyRepresentative(rowsArray.at(i), row.directorFullName)">
                <mat-icon>business</mat-icon>
              </button>
            }
          </td>
        </ng-container>

        <!-- Tenure Column (computed) -->
        <ng-container matColumnDef="tenure">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Tenure</th>
          <td mat-cell *matCellDef="let row" class="cell-readonly">{{ computeTenure(row.hireDate) }}</td>
        </ng-container>

        <!-- Hire Date Column -->
        <ng-container matColumnDef="hire">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Hire</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" formControlName="hireDate" class="ht-input ht-date-input"
                   [title]="getEndDateTooltip(row)"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onHireDateInput($event, row)">
          </td>
        </ng-container>

        <!-- CEO Hire Date Column -->
        <ng-container matColumnDef="ceoHireDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>CEO Hire Date</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" formControlName="ceoHireDate" class="ht-input ht-date-input"
                   [title]="getCeoEndDateTooltip(row)"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onCeoHireDateInput($event, row)">
          </td>
        </ng-container>

        <!-- Director Attends Column -->
        <ng-container matColumnDef="directorAttends">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Dir. Attends</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" inputmode="numeric" appNumberFormat formControlName="directorAttends" class="ht-input">
          </td>
        </ng-container>

        <!-- Meetings Total Column -->
        <ng-container matColumnDef="meetingsTotal">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Out of</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" inputmode="numeric" appNumberFormat formControlName="meetingsTotal" class="ht-input">
          </td>
        </ng-container>

        <!-- Percentage Column -->
        <ng-container matColumnDef="percentage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" inputmode="numeric" appNumberFormat [min]="0" [max]="100" [decimals]="2" formControlName="directorAttendsPercentage" class="ht-input">
          </td>
        </ng-container>

        <!-- O.P.B. Column -->
        <ng-container matColumnDef="connections">
          <th mat-header-cell *matHeaderCellDef>O.P.B.</th>
          <td mat-cell *matCellDef="let row" class="cell-readonly cell-center"
              [class.cell-clickable]="!isReadOnly()"
              [title]="getConnectionsTooltip(row)" (click)="isReadOnly() ? null : onOpenConnections(row)">{{ getConnectionsCount(row) }}</td>
        </ng-container>

        <!-- Delete Column -->
        <ng-container matColumnDef="delete">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            @if (!isReadOnly()) {
              <button mat-icon-button color="warn" (click)="onDelete(row)" class="delete-btn">
                <mat-icon>delete</mat-icon>
              </button>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.new-row]="!row.boardMemberId" [class.row-highlighted]="row.highlighted"></tr>
      </table>
    </div>
    }

    <div class="board-summary-row">
      <div class="summary-field">
        <label>Meeting Attendance Average</label>
        <div class="summary-field-values">
          <input type="text" class="ht-input summary-input" [value]="formatPercentage(boardAttendanceAverage)" readonly>
          @if (otherMeasurement) {
            <span class="ht-other">{{ formatPercentage(otherBoardAttendanceAverage) }}</span>
          }
        </div>
      </div>
      <div class="summary-field">
        <label>Meetings Frequency</label>
        <div class="summary-field-values">
          <input type="text" inputmode="numeric" appNumberFormat formControlName="meetingsFrequency" class="ht-input summary-input">
          @if (otherMeasurement) {
            <span class="ht-other">{{ formatNumber(otherData?.meetingsFrequency) }}</span>
          }
        </div>
      </div>
      <div class="summary-field-checkbox">
        <mat-checkbox formControlName="hasBoardGuidelines">Governance/Board Guidelines</mat-checkbox>
      </div>
      <div class="summary-field-checkbox">
        <mat-checkbox formControlName="hasCodeOfEthics">Code of Ethics</mat-checkbox>
      </div>
    </div>

    @if (!isReadOnly()) {
      <div class="table-actions">
        <button mat-raised-button color="primary" (click)="onInsert()">Add</button>
        @if (selectedBoardMembers.length > 0) {
          <button mat-raised-button color="primary" type="button" class="nrg-destructive-primary-button" (click)="onDeleteSelectedBoardMembers()">
            <mat-icon>delete</mat-icon>
            Delete selected ({{ selectedBoardMembers.length }})
          </button>
        }
      </div>
    }
  </div>

  <!-- Management Board (Dual mode only) -->
  @if (companyBoardType !== 0) {
    <div class="board-section">
      <h3 class="section-title">Management Board</h3>

      @if (managementDataSource.data.length === 0) {
        <div class="no-data-message">No data available</div>
      } @else {
      <div class="table-wrapper">
        <table mat-table [dataSource]="managementDataSource" class="board-table" formArrayName="managementRows"
               matSort #managementSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onManagementSort($event)">

          <!-- # Column -->
          <ng-container matColumnDef="index">
            <th mat-header-cell *matHeaderCellDef>#</th>
            <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
            <td mat-cell *matCellDef="let row">
            <span class="name-cell">
              <a [routerLink]="['/directors/edit', row.directorId]" [queryParams]="{ fromCompany: companyId, fromCompanyName: companyName(), fromYear: selectedYear }" class="director-link-btn" title="Edit director" (click)="$event.stopPropagation()">
                <mat-icon>open_in_new</mat-icon>
              </a>
              {{ row.directorFullName }}
            </span>
          </td>
          </ng-container>

          <!-- Classification Column -->
          <ng-container matColumnDef="classification">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Classification</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <select formControlName="classification" class="ht-select" [title]="getClassificationLabel(row.classification)">
                <option value=""></option>
                @for (opt of classificationOptions; track opt.value) {
                  <option [value]="opt.value" [title]="opt.label">{{ opt.label }}</option>
                }
              </select>
            </td>
          </ng-container>

          <!-- Occupation Column -->
          <ng-container matColumnDef="occupation">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <select formControlName="occupationId" class="ht-select" [title]="getOccupationName(row.occupationId)">
                <option value=""></option>
                @for (occ of occupations; track occ.id) {
                  <option [value]="occ.id" [title]="occ.name">{{ occ.name }}</option>
                }
              </select>
            </td>
          </ng-container>

          <!-- Company Representative Column -->
          <ng-container matColumnDef="companyRepresentative">
            <th mat-header-cell *matHeaderCellDef>C.R.</th>
            <td mat-cell *matCellDef="let row; let i = index" class="cell-center">
              @if (!isReadOnly() && isCompanyRepresentativeOccupation(managementRowsArray.at(i).get('occupationId')?.value)) {
                <button mat-icon-button type="button" class="cr-btn"
                        [class.has-value]="hasCompanyRepresentative(managementRowsArray.at(i))"
                        [title]="managementRowsArray.at(i).get('companyRepresentative')?.value || 'Add company representative'"
                        (click)="onEditCompanyRepresentative(managementRowsArray.at(i), row.directorFullName)">
                  <mat-icon>business</mat-icon>
                </button>
              }
            </td>
          </ng-container>

          <!-- Tenure Column (computed) -->
          <ng-container matColumnDef="tenure">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Tenure</th>
            <td mat-cell *matCellDef="let row" class="cell-readonly">{{ computeTenure(row.hireDate) }}</td>
          </ng-container>

          <!-- Hire Date Column -->
          <ng-container matColumnDef="hire">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Hire</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <input type="text" formControlName="hireDate" class="ht-input ht-date-input"
                     [title]="getEndDateTooltip(row)"
                     maxlength="10"
                     (input)="onHireDateInput($event, row)">
            </td>
          </ng-container>

          <!-- CEO Hire Date Column -->
          <ng-container matColumnDef="ceoHireDate">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>CEO Hire Date</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <input type="text" formControlName="ceoHireDate" class="ht-input ht-date-input"
                     [title]="getCeoEndDateTooltip(row)"
                     maxlength="10"
                     (input)="onCeoHireDateInput($event, row)">
            </td>
          </ng-container>

          <!-- Director Attends Column -->
          <ng-container matColumnDef="directorAttends">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Dir. Attends</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <input type="text" inputmode="numeric" appNumberFormat formControlName="directorAttends" class="ht-input">
            </td>
          </ng-container>

          <!-- Meetings Total Column -->
          <ng-container matColumnDef="meetingsTotal">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Out of</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <input type="text" inputmode="numeric" appNumberFormat formControlName="meetingsTotal" class="ht-input">
            </td>
          </ng-container>

          <!-- Percentage Column -->
          <ng-container matColumnDef="percentage">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <input type="text" inputmode="numeric" appNumberFormat [min]="0" [max]="100" [decimals]="2" formControlName="directorAttendsPercentage" class="ht-input">
            </td>
          </ng-container>

          <!-- O.P.B. Column -->
          <ng-container matColumnDef="connections">
            <th mat-header-cell *matHeaderCellDef>O.P.B.</th>
            <td mat-cell *matCellDef="let row" class="cell-readonly cell-center"
                [class.cell-clickable]="!isReadOnly()"
                [title]="getConnectionsTooltip(row)" (click)="isReadOnly() ? null : onOpenConnections(row)">{{ getConnectionsCount(row) }}</td>
          </ng-container>

          <!-- Delete Column -->
          <ng-container matColumnDef="delete">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              @if (!isReadOnly()) {
                <button mat-icon-button color="warn" (click)="onDelete(row)" class="delete-btn">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.new-row]="!row.boardMemberId" [class.row-highlighted]="row.highlighted"></tr>
        </table>
      </div>
      }

      <div class="board-summary-row">
        <div class="summary-field">
          <label>Meeting Attendance Average</label>
          <div class="summary-field-values">
            <input type="text" class="ht-input summary-input" [value]="formatPercentage(managementAttendanceAverage)" readonly>
            @if (otherMeasurement) {
              <span class="ht-other">{{ formatPercentage(otherManagementAttendanceAverage) }}</span>
            }
          </div>
        </div>
        <div class="summary-field">
          <label>Meetings Frequency</label>
          <div class="summary-field-values">
            <input type="text" inputmode="numeric" appNumberFormat formControlName="meetingsFrequencyManagement" class="ht-input summary-input">
            @if (otherMeasurement) {
              <span class="ht-other">{{ formatNumber(otherData?.meetingsFrequencyManagement) }}</span>
            }
          </div>
        </div>
      </div>

      @if (!isReadOnly()) {
        <div class="table-actions">
          <button mat-raised-button color="primary" (click)="onInsertManagement()">Add</button>
          @if (selectedManagementMembers.length > 0) {
            <button mat-raised-button color="primary" type="button" class="nrg-destructive-primary-button" (click)="onDeleteSelectedManagementMembers()">
              <mat-icon>delete</mat-icon>
              Delete selected ({{ selectedManagementMembers.length }})
            </button>
          }
        </div>
      }
    </div>
  }

  <!-- Keywords Section -->
  @if (showKeywords) {
    <div class="board-section keywords-section" formGroupName="keywords">
      <div class="keywords-header">
        <span>Keywords</span>
      </div>
      <div class="keywords-fields">
        <app-keyword-input name="meetings" label="Board Meetings" />
        <app-keyword-input name="attendance" label="Board Attendance" />
        <app-keyword-input name="executiveManagement" label="Executive Management" />
      </div>
    </div>
  }

  <!-- Executive Management -->
  <div class="board-section">
    <h3 class="section-title">Executive Management</h3>

    @if (executiveDataSource.data.length === 0) {
      <div class="no-data-message">No data available</div>
    } @else {
    <div class="table-wrapper">
      <table mat-table [dataSource]="executiveDataSource" class="board-table" formArrayName="executiveRows"
             matSort #executiveSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onExecutiveSort($event)">

        <!-- # Column -->
        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
        </ng-container>

        <!-- Name Column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
          <td mat-cell *matCellDef="let row">
            <span class="name-cell">
              <a [routerLink]="['/directors/edit', row.directorId]" [queryParams]="{ fromCompany: companyId, fromCompanyName: companyName(), fromYear: selectedYear }" class="director-link-btn" title="Edit director" (click)="$event.stopPropagation()">
                <mat-icon>open_in_new</mat-icon>
              </a>
              {{ row.directorFullName }}
            </span>
          </td>
        </ng-container>

        <!-- Occupation Column -->
        <ng-container matColumnDef="occupation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="occupationId" class="ht-select" [title]="getOccupationName(row.occupationId)">
              <option value=""></option>
              @for (occ of occupations; track occ.id) {
                <option [value]="occ.id" [title]="occ.name">{{ occ.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <!-- Tenure Column -->
        <ng-container matColumnDef="tenure">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Tenure</th>
          <td mat-cell *matCellDef="let row" class="cell-readonly">{{ computeTenure(row.hireDate) }}</td>
        </ng-container>

        <!-- Hire Date Column -->
        <ng-container matColumnDef="hire">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Hire</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" formControlName="hireDate" class="ht-input ht-date-input"
                   [title]="getEndDateTooltip(row)"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onHireDateInput($event, row)">
          </td>
        </ng-container>

        <!-- CEO Hire Date Column -->
        <ng-container matColumnDef="ceoHireDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>CEO Hire Date</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <input type="text" formControlName="ceoHireDate" class="ht-input ht-date-input"
                   [title]="getCeoEndDateTooltip(row)"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onCeoHireDateInput($event, row)">
          </td>
        </ng-container>

        <!-- O.P.B. Column -->
        <ng-container matColumnDef="connections">
          <th mat-header-cell *matHeaderCellDef>O.P.B.</th>
          <td mat-cell *matCellDef="let row" class="cell-readonly cell-center"
              [class.cell-clickable]="!isReadOnly()"
              [title]="getConnectionsTooltip(row)" (click)="isReadOnly() ? null : onOpenConnections(row)">{{ getConnectionsCount(row) }}</td>
        </ng-container>

        <!-- Delete Column -->
        <ng-container matColumnDef="delete">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            @if (!isReadOnly()) {
              <button mat-icon-button color="warn" (click)="onDeleteExecutive(row)" class="delete-btn">
                <mat-icon>delete</mat-icon>
              </button>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="executiveDisplayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: executiveDisplayedColumns;" [class.new-row]="!row.executiveMemberId" [class.row-highlighted]="row.highlighted"></tr>
      </table>
    </div>
    }

    @if (!isReadOnly()) {
      <div class="table-actions">
        <button mat-raised-button color="primary" (click)="onInsertExecutive()">Add</button>
        @if (selectedExecutiveMembers.length > 0) {
          <button mat-raised-button color="primary" type="button" class="nrg-destructive-primary-button" (click)="onDeleteSelectedExecutiveMembers()">
            <mat-icon>delete</mat-icon>
            Delete selected ({{ selectedExecutiveMembers.length }})
          </button>
        }
      </div>
    }
  </div>

  <!-- Comments -->
  <section class="section-container nrg-border-radius-second-level comments-section">
    <div class="section-header">
      <span>Comments</span>
    </div>
    <div class="section-content">
      <mat-form-field appearance="fill" class="form-field">
        <mat-label>Comments</mat-label>
        <textarea matInput formControlName="comments" rows="3"></textarea>
      </mat-form-field>
    </div>
  </section>

</div>
